/**
 * localStorage that never throws.
 *
 * Some browsers the app runs in have no usable localStorage. The one that made
 * this necessary is the signage player on the club's Fire TV: an app that shows
 * pages in Android's embedded browser, where storage is off unless the app turns
 * it on, so `window.localStorage` is null. The sign-in service reads it on every
 * page load, so one unguarded read took the whole app down to a blank screen,
 * public TV pages included. Private windows and full quotas fail the same way,
 * more quietly.
 *
 * When storage is missing or refuses, values are kept in memory for this page
 * load instead. Nothing persists, which is fine for a screen that is never
 * signed in.
 */

const memory = new Map();

function store() {
  try {
    const storage = window.localStorage;
    if (!storage) return null;
    // Some browsers hand back an object that throws on first use.
    const probe = '__storage_probe__';
    storage.setItem(probe, probe);
    storage.removeItem(probe);
    return storage;
  } catch (error) {
    return null;
  }
}

let resolved;
const backing = () => {
  if (resolved === undefined) resolved = store();
  return resolved;
};

export const safeStorage = {
  /** False when values only live in memory for this page load. */
  get persistent() {
    return Boolean(backing());
  },

  getItem(key) {
    const storage = backing();
    if (storage) {
      try {
        return storage.getItem(key);
      } catch (error) {
        // Fall through to memory.
      }
    }
    return memory.has(key) ? memory.get(key) : null;
  },

  setItem(key, value) {
    const text = String(value);
    memory.set(key, text);
    const storage = backing();
    if (!storage) return;
    try {
      storage.setItem(key, text);
    } catch (error) {
      // Quota or a storage that stopped working: memory still has it.
    }
  },

  removeItem(key) {
    memory.delete(key);
    const storage = backing();
    if (!storage) return;
    try {
      storage.removeItem(key);
    } catch (error) {
      // Nothing to do.
    }
  }
};

export default safeStorage;
